<script setup lang="ts">
  /**
   * Password strength — 03-components/17-password-strength · OSCloud DS 1.1.154
   *
   * Live password policy feedback: a segmented strength meter plus a checklist
   * that ticks each rule as it is satisfied. The rules live in
   * BfPasswordStrengthRules, so a page can gate its submit on the same policy.
   *
   * Two configurations. The bare block reports on a password being entered.
   * With the `password` and `confirmation` slots it is --with-confirmation: a
   * password being SET, where the field pair is part of the block, adjacent in
   * __fields, and __match carries whether the two entries agree.
   *
   * The block states its own colours: data-strength on the root is the number
   * of filled segments (empty|1|2|3|4) and the CSS tones the segments and the
   * tier name from it — red at one, orange at two or three, green at four.
   *
   * Purely presentational: it reports on the password it is given and owns no
   * validation authority. Submission is still gated by the form itself.
   */
  import { computed, useSlots } from 'vue'
  import BfHint from '@/components/ui/bahnhof/BfHint.vue'
  import { ALLOWED_PASSWORD_SYMBOLS, PASSWORD_RULES } from '@/components/ui/bahnhof/BfPasswordStrengthRules'

  interface PasswordStrengthTier {
    label: string
    filledSegments: number
  }

  type MatchState = 'pending' | 'match' | 'mismatch'

  const props = withDefaults(
    defineProps<{
      password?: string
      /** Lets a password field point `aria-describedby` at this block. */
      id?: string
      /** The confirmation entry, compared with `password` for the match line. */
      confirmation?: string
      /**
       * The confirmation field has been left once. Until then a mismatch is still being typed
       * and the match line stays silent.
       */
      confirmationTouched?: boolean
      /** The match line's id, for the confirmation field's `aria-describedby`. */
      matchId?: string
    }>(),
    {
      password: '',
      id: undefined,
      confirmation: '',
      confirmationTouched: false,
      matchId: undefined,
    },
  )

  const slots = useSlots()

  const SEGMENT_COUNT = 4

  /* Indexed by how many rules pass (0–5). The meter is out of four and the policy
     has five rules, so Fair and Good share three segments and only Strong — every
     rule met — fills the fourth. */
  const PASSWORD_STRENGTH_TIERS: PasswordStrengthTier[] = [
    { label: '—', filledSegments: 0 },
    { label: 'Very weak', filledSegments: 1 },
    { label: 'Weak', filledSegments: 2 },
    { label: 'Fair', filledSegments: 3 },
    { label: 'Good', filledSegments: 3 },
    { label: 'Strong', filledSegments: 4 },
  ]

  const segments = Array.from({ length: SEGMENT_COUNT }, (_, index) => index)

  const isPaired = computed(() => Boolean(slots.confirmation))

  const ruleResults = computed(() => PASSWORD_RULES.map(rule => ({ ...rule, met: rule.isMet(props.password) })))

  const metRuleCount = computed(() => ruleResults.value.filter(rule => rule.met).length)

  const tier = computed(() => PASSWORD_STRENGTH_TIERS[props.password ? metRuleCount.value : 0])

  const strength = computed(() => (props.password ? String(tier.value.filledSegments) : 'empty'))

  /* pending = nothing to say yet: no confirmation, or a mismatch still being typed. */
  const matchState = computed<MatchState>(() => {
    const isMismatch = props.confirmation !== props.password
    if (!props.confirmation || (isMismatch && !props.confirmationTouched)) return 'pending'
    return isMismatch ? 'mismatch' : 'match'
  })
</script>

<template>
  <div
    :id="id"
    class="password-strength"
    :class="{ 'password-strength--with-confirmation': isPaired }"
    :data-strength="strength"
  >
    <template v-if="isPaired">
      <div class="password-strength__fields">
        <slot name="password" />
        <slot name="confirmation" />
      </div>
      <p :id="matchId" class="password-strength__match" :data-state="matchState" aria-live="polite">
        <span class="password-strength__match-icon" aria-hidden="true">
          <svg
            v-if="matchState === 'match'"
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="square"
            focusable="false"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="square"
            focusable="false"
          >
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </span>
        <span>{{ matchState === 'match' ? 'Both entries match' : 'The two passwords do not match' }}</span>
      </p>
    </template>
    <slot v-else name="password" />

    <!-- The track is always rendered: four grey segments at rest say the meter is out of four
         and that nothing is met yet. Only the tier label waits for a first character. -->
    <div class="password-strength__track">
      <span
        v-for="segment in segments"
        :key="segment"
        class="password-strength__segment"
        :data-filled="password && segment < tier.filledSegments ? '' : undefined"
      />
    </div>
    <p class="password-strength__summary" aria-live="polite">
      <span>{{ password ? 'Password strength' : 'Your password must include:' }}</span>
      <span v-if="password" class="password-strength__level">{{ tier.label }}</span>
    </p>

    <ul class="password-strength__requirements">
      <li
        v-for="rule in ruleResults"
        :key="rule.id"
        class="password-strength__requirement"
        :class="{
          'password-strength__requirement--satisfied': rule.met,
          'password-strength__requirement--wide': rule.id === 'symbol',
        }"
      >
        <span class="password-strength__tick" aria-hidden="true">
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3.5"
            stroke-linecap="square"
            focusable="false"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </span>
        {{ rule.label }}
        <span class="sr-only">{{ rule.met ? '— requirement met' : '— requirement not met' }}</span>

        <BfHint v-if="rule.id === 'symbol'" label="Which special characters are allowed">
          {{ ALLOWED_PASSWORD_SYMBOLS }}
        </BfHint>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
  .password-strength__track {
    display: flex;
    gap: 5px;
    margin-bottom: 7px;
  }

  .password-strength__segment {
    flex: 1;
    height: 5px;
    border-radius: 3px;
    background: var(--bf-line);
    transition: background var(--bf-dur-base) var(--bf-ease);
  }

  /* Grey – red – orange – green: every filled segment shares one tone, set by how far
     the fill reaches. */
  .password-strength[data-strength='1'] .password-strength__segment[data-filled] {
    background: var(--bf-strength-1-bar);
  }

  .password-strength[data-strength='2'] .password-strength__segment[data-filled],
  .password-strength[data-strength='3'] .password-strength__segment[data-filled] {
    background: var(--bf-strength-2-bar);
  }

  .password-strength[data-strength='4'] .password-strength__segment[data-filled] {
    background: var(--bf-strength-4-bar);
  }

  .password-strength__summary {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 0 0 20px;
    padding-top: 20px;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    color: var(--bf-fg-2);
  }

  .password-strength__level {
    font-family: var(--bf-font-display);
    font-weight: var(--bf-fw-bold);
    color: var(--bf-fg-2);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  /* The tier name takes the meter's tone — the AA-safe -text pair of the -bar in use. */
  .password-strength[data-strength='1'] .password-strength__level {
    color: var(--bf-strength-1-text);
  }

  .password-strength[data-strength='2'] .password-strength__level,
  .password-strength[data-strength='3'] .password-strength__level {
    color: var(--bf-strength-2-text);
  }

  .password-strength[data-strength='4'] .password-strength__level {
    color: var(--bf-strength-4-text);
  }

  /* The pair: two fields as one unit, at the fieldset's field rhythm. Nothing goes
     between the two. */
  .password-strength__fields {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-3);
  }

  /* The pair's own state line — not a rule in the checklist: it can be fixed in the
     second field alone, so it is read there, with the pair. */
  .password-strength__match {
    display: flex;
    gap: 8px;
    align-items: center;
    margin: 10px 0 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    color: var(--bf-fg-2);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  .password-strength__match[data-state='pending'] {
    display: none;
  }

  .password-strength__match[data-state='match'] {
    color: var(--bf-strength-4-text);
  }

  .password-strength__match[data-state='mismatch'] {
    color: var(--bf-form-invalid-text);
  }

  .password-strength__match-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    color: var(--bf-white);
    background: currentColor;
    border-radius: 50%;
  }

  .password-strength__match[data-state='match'] .password-strength__match-icon {
    background: var(--bf-strength-4-text);
  }

  .password-strength__match[data-state='mismatch'] .password-strength__match-icon {
    background: var(--bf-form-invalid-text);
  }

  /* The match line sits between the fields and the track; the meter must not run into
     its descenders. The summary's own padding-top is the track → summary gap. */
  .password-strength--with-confirmation .password-strength__track {
    margin-top: var(--bf-sp-3);
  }

  .password-strength__requirements {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 7px var(--bf-sp-3);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .password-strength__requirement {
    display: flex;
    gap: 8px;
    align-items: center;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs);
    color: var(--bf-fg-2);
    transition: color var(--bf-dur-base) var(--bf-ease);
  }

  /* The symbol rule keeps the full row: it is the last one, so the hint it carries
     opens over the fields below rather than over the checklist. */
  .password-strength__requirement--wide {
    grid-column: 1 / -1;
  }

  .password-strength__requirement--satisfied {
    color: var(--bf-off-black);
  }

  .password-strength__tick {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    color: transparent;
    border: 1.5px solid var(--bf-dusky-2);
    border-radius: 50%;
    transition:
      color var(--bf-dur-base) var(--bf-ease),
      background var(--bf-dur-base) var(--bf-ease),
      border-color var(--bf-dur-base) var(--bf-ease);
  }

  .password-strength__requirement--satisfied .password-strength__tick {
    color: var(--bf-white);
    background: var(--bf-strength-4-text);
    border-color: var(--bf-strength-4-text);
  }

  @media (max-width: 480px) {
    .password-strength__requirements {
      grid-template-columns: 1fr;
    }
  }
</style>
